import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import { type AssetComment, type Stored } from "@overmap-ai/common"
import { selectCurrentUser } from "@overmap-ai/core"
import { memo, type ReactElement, useCallback } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector } from "../../store"

interface AssetCommentDropdownMenuProps
	extends MenuRootProps, Omit<MenuClickTriggerProps, "children">, MenuVariantProps {
	trigger: ReactElement
	assetComment: Stored<AssetComment>
	onResolvedChange: (assetComment: Stored<AssetComment>, resolved: boolean) => void
	onEdit: () => void
	onDelete: (assetComment: Stored<AssetComment>) => void
}

export const AssetCommentDropdownMenu = memo((props: AssetCommentDropdownMenuProps) => {
	const { trigger, disabled, assetComment, onResolvedChange, onEdit, onDelete, size, radius, variant, accentColor } =
		props
	const currentUser = useAppSelector(selectCurrentUser)

	const handleResolvedChange = useCallback(() => {
		onResolvedChange(assetComment, !assetComment.resolved)
	}, [assetComment, onResolvedChange])

	const handleDelete = useCallback(() => {
		onDelete(assetComment)
	}, [assetComment, onDelete])

	return (
		<Menu.Root>
			<Menu.ClickTrigger disabled={disabled}>{trigger}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} accentColor={accentColor} variant={variant}>
				<Menu.Group>
					<Menu.Item onSelect={handleResolvedChange}>
						<LuIcon icon={assetComment.resolved ? modelIconMapping.refresh : modelIconMapping.check} />
						{assetComment.resolved ? "Unresolve" : "Resolve"}
					</Menu.Item>
					{currentUser?.id === assetComment.created_by && (
						<>
							<Menu.Item onSelect={onEdit}>
								<LuIcon icon={modelIconMapping.edit} />
								Edit
							</Menu.Item>
							<Menu.Item onSelect={handleDelete}>
								<LuIcon icon={modelIconMapping.delete} />
								Delete
							</Menu.Item>
						</>
					)}
				</Menu.Group>
			</Menu.Content>
		</Menu.Root>
	)
})
AssetCommentDropdownMenu.displayName = "AssetCommentDropdownMenu"
